import { useMemo } from "react";
import { BrandDetails } from "../types/add-brand-types";
import { ContentLog } from "../types/dashboard";
import { WeeklyPlan, WeeklyPlanItem } from "../types/weekly-plan";
import { WeeklyPlansByBrandId } from "./useWeeklyPlansForBrands";

export type NormalizedContentStatus =
  | "SCHEDULED"
  | "DRAFT"
  | "PUBLISHED"
  | "PROCESSING"
  | "FAILED"
  | "PARTIAL_FAILURE"
  | "UNKNOWN";

export interface WeeklyWorkQueueContextReadiness {
  label: string;
  tone: "ready" | "partial" | "missing";
}

export interface WeeklyWorkQueueRow {
  brandId: string;
  brandName: string;
  contextReadiness: WeeklyWorkQueueContextReadiness;
  hasPersistedPlan: boolean;
  queueSourceLabel: string;
  openThisWeek: number;
  scheduledThisWeek: number;
  publishedThisWeek: number;
  derivedItems: string[];
}

const normalizeStatus = (status: string | null | undefined): NormalizedContentStatus => {
  const normalized = (status ?? "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

  switch (normalized) {
    case "SCHEDULED":
    case "DRAFT":
    case "PUBLISHED":
    case "PROCESSING":
    case "FAILED":
    case "PARTIAL_FAILURE":
      return normalized;
    default:
      return "UNKNOWN";
  }
};

const startOfWeek = (input: Date): Date => {
  const date = new Date(input);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + diff);
  return date;
};

const endOfWeek = (input: Date): Date => {
  const date = startOfWeek(input);
  date.setDate(date.getDate() + 7);
  return date;
};

const toDate = (value: string | number | null | undefined): Date | null => {
  if (value == null || value === "") {
    return null;
  }

  const date =
    typeof value === "number"
      ? new Date(value < 1_000_000_000_000 ? value * 1000 : value)
      : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const isWithinRange = (value: Date | null, start: Date, end: Date) => {
  if (!value) {
    return false;
  }

  return value >= start && value < end;
};

const getContextReadiness = (
  brand: BrandDetails,
): WeeklyWorkQueueContextReadiness => {
  const strategySignals = [
    brand.marketing_strategy?.brand_name,
    brand.marketing_strategy?.brand_url,
    brand.marketing_strategy?.tone,
    brand.marketing_strategy?.target_audience,
    brand.marketing_strategy?.competitors,
  ].filter(Boolean).length;
  const knowledgeSignals = Object.keys(brand.knowledge_base ?? {}).length;
  const summarySignals = [
    brand.product_summary,
    Object.keys(brand.latest_insights ?? {}).length > 0 ? "latest_insights" : "",
  ].filter(Boolean).length;

  if (knowledgeSignals > 0 || summarySignals > 0) {
    return {
      label: "Context ready",
      tone: "ready",
    };
  }

  if (strategySignals >= 3) {
    return {
      label: "Context partial",
      tone: "partial",
    };
  }

  return {
    label: "Context thin",
    tone: "missing",
  };
};

const getLatestLog = (logs: ContentLog[]): ContentLog | null => {
  if (logs.length === 0) {
    return null;
  }

  return [...logs].sort((left, right) => {
    const leftTime =
      toDate(left.published_at)?.getTime() ??
      toDate(left.content?.scheduledFor)?.getTime() ??
      toDate(left.created_at)?.getTime() ??
      0;
    const rightTime =
      toDate(right.published_at)?.getTime() ??
      toDate(right.content?.scheduledFor)?.getTime() ??
      toDate(right.created_at)?.getTime() ??
      0;

    return rightTime - leftTime;
  })[0];
};

const buildDerivedItems = ({
  brand,
  contextReadiness,
  latestLog,
  scheduledThisWeek,
  publishedThisWeek,
}: {
  brand: BrandDetails;
  contextReadiness: WeeklyWorkQueueContextReadiness;
  latestLog: ContentLog | null;
  scheduledThisWeek: number;
  publishedThisWeek: number;
}): string[] => {
  const latestStatus = normalizeStatus(latestLog?.status);
  const latestTopic = latestLog?.topic?.trim();

  const contextItem =
    contextReadiness.tone === "ready"
      ? "Review current context before moving posts forward."
      : contextReadiness.tone === "partial"
        ? "Tighten brand context before a heavier posting week."
        : "Add more product and audience context before scaling output.";

  let productionItem = `Create the next post for ${brand.brand_name}.`;
  if (latestTopic) {
    switch (latestStatus) {
      case "DRAFT":
        productionItem = `Finish the draft for "${latestTopic}".`;
        break;
      case "SCHEDULED":
        productionItem = `Review the scheduled post "${latestTopic}".`;
        break;
      case "PROCESSING":
        productionItem = `Check the in-flight post "${latestTopic}".`;
        break;
      case "FAILED":
      case "PARTIAL_FAILURE":
        productionItem = `Repair and resend "${latestTopic}".`;
        break;
      case "PUBLISHED":
        productionItem = `Create a follow-up to "${latestTopic}".`;
        break;
      default:
        productionItem = `Create the next post for ${brand.brand_name}.`;
        break;
    }
  }

  let throughputItem = "Queue one publish-ready post for this week.";
  if (scheduledThisWeek > 0 && publishedThisWeek > 0) {
    throughputItem = `Track ${scheduledThisWeek} scheduled and ${publishedThisWeek} published item(s) this week.`;
  } else if (scheduledThisWeek > 0) {
    throughputItem = `Keep ${scheduledThisWeek} scheduled item(s) moving this week.`;
  } else if (publishedThisWeek > 0) {
    throughputItem = `Turn ${publishedThisWeek} published item(s) into the next draft.`;
  }

  return [contextItem, productionItem, throughputItem];
};

const PLAN_STATUS_LABELS: Record<WeeklyPlanItem["status"], string> = {
  planned: "Planned",
  drafted: "Drafted",
  scheduled: "Scheduled",
  published: "Published",
  skipped: "Skipped",
};

const formatPlanItemType = (item: WeeklyPlanItem): string => {
  if (item.content_type === "seo_article") {
    return "SEO";
  }
  return item.platform ? item.platform.toUpperCase() : "Social";
};

const buildPersistedPlanItems = (plan: WeeklyPlan): string[] => {
  if (plan.items.length === 0) {
    return ["Generate plan items for this week."];
  }

  return [...plan.items]
    .sort(
      (left, right) =>
        (toDate(left.planned_for)?.getTime() ?? 0) -
        (toDate(right.planned_for)?.getTime() ?? 0)
    )
    .map(
      (item) =>
        `${PLAN_STATUS_LABELS[item.status]} - ${formatPlanItemType(item)} - ${
          item.topic
        }`
    );
};

const countPlanStatus = (
  items: WeeklyPlanItem[],
  statuses: WeeklyPlanItem["status"][]
): number => items.filter((item) => statuses.includes(item.status)).length;

export const deriveWeeklyWorkQueue = (
  brands: BrandDetails[] | null | undefined,
  logs: ContentLog[] | null | undefined,
  weeklyPlansOrNow: WeeklyPlansByBrandId | Date = new Date(),
  now: Date = new Date(),
): WeeklyWorkQueueRow[] => {
  if (!brands || brands.length === 0) {
    return [];
  }

  const weeklyPlansByBrandId =
    weeklyPlansOrNow instanceof Date ? undefined : weeklyPlansOrNow;
  const effectiveNow = weeklyPlansOrNow instanceof Date ? weeklyPlansOrNow : now;
  const weekStart = startOfWeek(effectiveNow);
  const weekEnd = endOfWeek(effectiveNow);

  return brands.map((brand) => {
    const brandLogs = (logs ?? []).filter((log) => log.product_id === brand.id);
    const contextReadiness = getContextReadiness(brand);
    const latestLog = getLatestLog(brandLogs);
    const persistedPlan = weeklyPlansByBrandId?.[brand.id];

    if (persistedPlan) {
      const plannedItems = persistedPlan.items ?? [];
      return {
        brandId: brand.id,
        brandName: brand.brand_name,
        contextReadiness,
        hasPersistedPlan: true,
        queueSourceLabel: "Persisted weekly plan",
        openThisWeek: countPlanStatus(plannedItems, ["planned", "drafted"]),
        scheduledThisWeek: countPlanStatus(plannedItems, ["scheduled"]),
        publishedThisWeek: countPlanStatus(plannedItems, ["published"]),
        derivedItems: buildPersistedPlanItems(persistedPlan),
      };
    }

    const scheduledThisWeek = brandLogs.filter((log) => {
      const status = normalizeStatus(log.status);
      return (
        status === "SCHEDULED" &&
        isWithinRange(toDate(log.content?.scheduledFor), weekStart, weekEnd)
      );
    }).length;

    const publishedThisWeek = brandLogs.filter((log) => {
      const status = normalizeStatus(log.status);
      return (
        status === "PUBLISHED" &&
        isWithinRange(toDate(log.published_at), weekStart, weekEnd)
      );
    }).length;

    return {
      brandId: brand.id,
      brandName: brand.brand_name,
      contextReadiness,
      hasPersistedPlan: false,
      queueSourceLabel: "Derived from history",
      openThisWeek: 0,
      scheduledThisWeek,
      publishedThisWeek,
      derivedItems: buildDerivedItems({
        brand,
        contextReadiness,
        latestLog,
        scheduledThisWeek,
        publishedThisWeek,
      }),
    };
  });
};

const useWeeklyWorkQueue = (
  brands: BrandDetails[] | null | undefined,
  logs: ContentLog[] | null | undefined,
  weeklyPlansByBrandId?: WeeklyPlansByBrandId,
) => {
  return useMemo(
    () => deriveWeeklyWorkQueue(brands, logs, weeklyPlansByBrandId),
    [brands, logs, weeklyPlansByBrandId]
  );
};

export { normalizeStatus };

export default useWeeklyWorkQueue;
